/** @jsxImportSource @emotion/react */
import React, { useEffect, useRef, useState } from 'react';
import { useFloating, autoPlacement, shift, autoUpdate } from '@floating-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DeleteConfirmation from 'components/DeleteConfirmation';
import { IoClose } from 'react-icons/io5';
import { css } from '@emotion/react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { EVENT_COLORS } from '../../../utils/constants';
import './style.scss';

dayjs.extend(utc);

const CardContainer = ({
  reference,
  open,
  calendarRef,
  children,
  handleCalendarEventDelete,
  showDeleteButton = false,
  disableOutsideClick = false,
  className = '',
  eventDetails = {},
  closeEventPopup,
}) => {
  const [showDeleteConfirm, setDeleteConfirm] = useState(false);
  const ref = useRef(null);

  const eventType = eventDetails?.extendedProps?.type;
  const eventColor =
    EVENT_COLORS[eventType] ||
    eventDetails?.backgroundColor ||
    EVENT_COLORS.default;

  const { refs, floatingStyles } = useFloating({
    whileElementsMounted: autoUpdate,
    middleware: [
      autoPlacement({
        crossAxis: true,
        padding: { top: 200, left: 200, right: 200 },
        boundary: document.querySelector('#calendar-wrapper'),
      }),
      shift(),
    ],
    elements: { open, reference },
  });

  const combineRefs = (...refList) => (node) => {
    refList.forEach((refItem) => {
      if (typeof refItem === 'function') refItem(node);
      else if (refItem) refItem.current = node;
    });
  };

  useEffect(() => {
    if (disableOutsideClick) return;

    const handleClickOutside = (event) => {
      const isDropdownClick =
        event.target.closest('.ap-dropdown__menu') ||
        event.target.classList.contains('ap-dropdown__menu');

      const isModalClick =
        event.target.closest('.delete-confirmation-modal') ||
        event.target.classList.contains('delete-confirmation-modal');

      if (
        ref.current &&
        !ref.current.contains(event.target) &&
        !calendarRef.current?.elRef?.current.contains(event.target) &&
        !isDropdownClick &&
        !isModalClick
      ) {
        event.stopPropagation();
        closeEventPopup();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref, disableOutsideClick]);

  if (!open) return null;

  return (
    <div
      ref={combineRefs(refs.setFloating, ref)}
      style={floatingStyles}
      className={clsx(
        'card-container',
        className,
      )}
      css={css`
        border-top: 10px solid ${eventColor};
      `}
    >
      {showDeleteButton ? (
        <>
          <i
            className="card-container__icon delete"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteConfirm(true);
            }}
          >
            <FontAwesomeIcon icon={['fal', 'times-circle']} />
          </i>
          <DeleteConfirmation
            show={showDeleteConfirm}
            handleClose={() => {
              setDeleteConfirm(false);
            }}
            handleConfirm={() => {
              handleCalendarEventDelete();
              setDeleteConfirm(false);
            }}
            deleteText="DELETE EVENT"
            size="xs"
          >
            <p>
              Deleting this calendar event will remove<br />
              it from your <b>account</b> forever.<br />
              Are you sure?
            </p>
          </DeleteConfirmation>
        </>
      ) : (
        <IoClose
          className="card-container__close"
          size={20}
          onClick={closeEventPopup}
        />
      )}

      {children}
    </div>
  );
};

export default CardContainer;
